import { tr } from '@/lib/i18n-core';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, BadgeCheck, LifeBuoy } from 'lucide-react';
import type { TenantLicenceCardDto } from '@his/shared';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, dateTime } from '@/lib/utils';
import { useGraceCodeMutation, useLicenceCardQuery } from '@/lib/updates-api';
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, SkeletonText, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[150px_1fr] items-start gap-3 py-2 text-sm">
      <span className="pt-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function licenceStateLabel(card: TenantLicenceCardDto): { label: string; variant: 'success' | 'warning' | 'destructive' } {
  const lifecycle = card.lifecycle;
  if (!lifecycle) return { label: tr('admin', 'active_2'), variant: 'success' };
  if (lifecycle.blocked) return { label: tr('admin', 'closed'), variant: 'destructive' };
  if (lifecycle.readOnly) return { label: tr('admin', 'read_only'), variant: 'warning' };
  if (lifecycle.status === 'trial') return { label: tr('admin', 'trial'), variant: 'success' };
  return { label: tr('admin', 'active_2'), variant: 'success' };
}

function GraceCodeForm() {
  const toast = useToast();
  const [code, setCode] = useState('');
  const apply = useGraceCodeMutation();
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        apply.mutate(code.trim(), {
          onSuccess: (result) => {
            setCode('');
            toast.success(tr('admin', 'grace_extended_until_x', { until: dateTime(result.until) }));
          },
          onError: (err) => toast.error(tr('admin', 'the_code_was_not_accepted'), describeError(err)),
        });
      }}
    >
      <Input
        value={code}
        onChange={(event) => setCode(event.target.value)}
        placeholder={tr('admin', 'paste_the_code_support_gave_you')}
        className="min-w-[240px] flex-1 font-mono text-xs"
        aria-label={tr('admin', 'offline_grace_code')}
      />
      <Button type="submit" variant="outline" size="sm" loading={apply.isPending} disabled={code.trim().length < 20}>
        {tr('admin', 'apply_code')}
      </Button>
    </form>
  );
}

export function LicenceCard() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const query = useLicenceCardQuery();
  const card = query.data;

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <BadgeCheck className="h-4 w-4 text-primary" aria-hidden /> {tr('admin', 'licence')}
        </CardTitle>
        <CardDescription>{tr('admin', 'what_this_facility_is_licensed_for')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isLoading && (
          <SkeletonText lines={3} />
        )}
        {!query.isLoading && !card && <p className="text-sm text-muted-foreground">{tr('admin', 'the_licence_could_not_be_read')}</p>}
        {card && (
          <>
            {card.problem && (
              <Alert variant="warning" className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
                <span>{card.problem}</span>
              </Alert>
            )}
            {card.lockReason && (
              <Alert variant="warning" className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
                <span>{card.lockReason}</span>
              </Alert>
            )}
            <div className="divide-y">
              <Line label={tr('admin', 'state')}>
                <Badge variant={licenceStateLabel(card).variant}>{licenceStateLabel(card).label}</Badge>
                {card.lifecycle?.trialEndsAt && <span className="ms-2 text-xs text-muted-foreground">{tr('admin', 'trial_ends')} {dateOnly(card.lifecycle.trialEndsAt)}</span>}
                {card.lifecycle?.paidUntil && <span className="ms-2 text-xs text-muted-foreground">{tr('admin', 'paid_until')} {dateOnly(card.lifecycle.paidUntil)}</span>}
              </Line>
              <Line label={tr('admin', 'packages')}>{card.packages.length > 0 ? card.packages.join(', ') : <span className="text-muted-foreground">—</span>}</Line>
              <Line label={tr('admin', 'modules')}>
                <div className="flex flex-wrap gap-1.5">
                  {card.modules
                    .filter((entry) => entry.state === 'enabled')
                    .map((entry) => (
                      <Badge key={entry.code} variant="default">
                        {entry.name}
                      </Badge>
                    ))}
                </div>
              </Line>
              {card.modules.some((entry) => entry.state === 'retired') && (
                <Line label={tr('admin', 'removed')}>
                  <ul className="space-y-1">
                    {card.modules
                      .filter((entry) => entry.state === 'retired')
                      .map((entry) => (
                        <li key={entry.code}>
                          {entry.name} <span className="text-xs text-muted-foreground">{tr('admin', 'records_stay_readable_and_can_be_exported')}</span>
                        </li>
                      ))}
                  </ul>
                </Line>
              )}
              <Line label={tr('admin', 'add_ons')}>
                <div className="flex flex-wrap gap-1.5">
                  {card.addOns.map((addOn) => (
                    <Badge key={addOn.code} variant={addOn.on ? 'success' : 'muted'}>
                      {addOn.name}: {addOn.on ? tr('admin', 'on') : tr('admin', 'off')}
                    </Badge>
                  ))}
                </div>
              </Line>
              {card.runtime === 'local' && (
                <Line label={tr('admin', 'offline_grace')}>
                  {card.graceDays !== null ? tr('admin', 'x_days_without_a_check_in', { graceDays: card.graceDays }) : '—'}
                  {card.graceExtensionUntil && <p className="text-xs text-muted-foreground">{tr('admin', 'extended_until')} {dateTime(card.graceExtensionUntil)}</p>}
                  {card.graceCodeUntil && <p className="text-xs text-muted-foreground">{tr('admin', 'grace_code_valid_until')} {dateTime(card.graceCodeUntil)}</p>}
                </Line>
              )}
              {card.runtime === 'local' && isSuperAdmin && (
                <Line label={tr('admin', 'grace_code')}>
                  <p className="mb-2 text-xs text-muted-foreground">
                    {tr('admin', 'for_an_internet_outage_support_can_give_you_a')}
                  </p>
                  <GraceCodeForm />
                </Line>
              )}
            </div>
            <details className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
              <summary className="cursor-pointer text-muted-foreground">{tr('admin', 'technical_details')}</summary>
              <div className="mt-2 space-y-1">
                <p>{tr('admin', 'licence_version')} <span className="font-mono">{card.licenceVersion ?? '—'}</span></p>
                <p>{tr('admin', 'received')} {card.receivedAt ? dateTime(card.receivedAt) : '—'}</p>
                <p>{tr('admin', 'source')} {card.source === 'licence' ? tr('admin', 'signed_licence') : tr('admin', 'settings_on_this_server')}{card.unsigned ? ' (unsigned, development build)' : ''}</p>
              </div>
            </details>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/support/tickets/new?category=licence_change')}>
                <LifeBuoy className="h-3.5 w-3.5" /> {tr('admin', 'request_a_change')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/support/tickets/new?category=payment_proof')}>
                {tr('admin', 'send_payment_proof')}
              </Button>
              {card.runtime === 'local' && (
                <span className="text-xs text-muted-foreground">{tr('admin', 'needs_this_server_to_be_online_without_internet')}</span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
